import { Fragment } from 'react';
import { FormDateRange } from 'src/components';
import {
  FormAutocomplete,
  FormEditableText,
  FormField,
  FormRichText,
  FormRichTextBubble,
  FormSwitch,
  FormTextField,
  FormToggleGroup,
} from 'src/components/Forms';
import { FormDatePicker } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { FormPhoneInput } from 'src/components/Forms/FormPhoneInput/form-phone-input';

export function getFormField(field: FormField, disabled?: boolean) {
  const {
    prepopulate,
    type,
    defaultValue,
    label,
    renderField,
    group,
    value,
    onChange,
    ...rest
  } = field;

  /**
   * Additional case-based destructuring is required, because otherwise TS can't determine which
   * set of `rest` properties is being spread in each specific case.
   * TODO: Review typings in order to find possible better typing declaration.
   */
  switch (type) {
    case 'autocomplete':
      return (
        <Fragment key={field.name + field.type}>
          <FormAutocomplete
            type={type}
            defaultValue={defaultValue}
            label={label}
            options={field.options}
            disabled={disabled}
            onChange={onChange}
            {...rest}
          />
        </Fragment>
      );
    case 'textfield':
      return (
        <Fragment key={field.name + field.type}>
          <FormTextField
            type={type}
            defaultValue={defaultValue}
            label={label}
            textFieldProps={field.textFieldProps}
            value={value}
            disabled={disabled}
            onChange={onChange}
            {...rest}
          />
        </Fragment>
      );
    case 'phone':
      return (
        <Fragment key={field.name + field.type}>
          <FormPhoneInput
            type={type}
            defaultValue={defaultValue}
            label={label}
            value={value}
            disabled={disabled}
            {...rest}
          />
        </Fragment>
      );
    case 'rich-text':
      return (
        <Fragment key={field.name + field.type}>
          <FormRichText
            type={type}
            defaultValue={defaultValue}
            disabled={disabled}
            value={value}
            {...rest}
          />
        </Fragment>
      );
    case 'rich-text-bubble':
      return (
        <Fragment key={field.name + field.type}>
          <FormRichTextBubble
            type={type}
            defaultValue={defaultValue}
            editable={!disabled}
            disabled={disabled}
            label={label}
            value={value}
            onChange={onChange}
            {...rest}
          />
        </Fragment>
      );
    case 'editable-text':
      return (
        <Fragment key={field.name + field.type}>
          <FormEditableText
            type={type}
            defaultValue={defaultValue}
            textFieldProps={field.textFieldProps}
            value={value}
            disabled={disabled}
            {...rest}
          />
        </Fragment>
      );
    case 'switch':
      return (
        <Fragment key={field.name + field.type}>
          <FormSwitch
            type={type}
            value={value}
            defaultValue={defaultValue}
            switchFieldProps={field.switchFieldProps}
            label={label}
            disabled={disabled}
            onChange={onChange}
            {...rest}
          />
        </Fragment>
      );
    case 'datepicker':
      return (
        <Fragment key={field.name}>
          <FormDatePicker
            type={type}
            value={value}
            defaultValue={defaultValue}
            label={label}
            disabled={disabled}
            {...rest}
          />
        </Fragment>
      );
    case 'toggle-group':
      return (
        <Fragment key={field.name + field.type}>
          <FormToggleGroup
            type={type}
            defaultValue={defaultValue}
            label={label}
            value={value}
            disabled={disabled}
            onChange={onChange}
            {...rest}
            // After the spread on purpose: `options` is not destructured out of
            // `field`, so `rest` carries the un-narrowed union and would override an
            // earlier prop.
            options={field.options}
          />
        </Fragment>
      );
    case 'daterange':
      return (
        <Fragment key={field.name + field.type}>
          <FormDateRange
            type={type}
            defaultValue={defaultValue}
            label={label}
            value={value}
            disabled={disabled}
            {...rest}
          />
        </Fragment>
      );
    default:
      return null;
  }
}
